import React, { useEffect, useMemo } from 'react';
import { Switch, Redirect, useParams } from 'react-router-dom';
import {
  LoginCallback,
  PostAuthentication,
  SingleSignon,
} from '@tekion/tap-components/middlewares/authentication';
import { Security } from '@okta/okta-react';
import { Route } from '@tekion/tap-components/middlewares/applicationMonitoring/ElasticAPM';
import ErrorPage from '@tekion/tap-components/atoms/ErrorPage';
import TokenManager from '@tekion/tap-components/utils/TokenManager';
import withLayout from '../../../hocs/withLayout.hoc';
import withLoadable from '../../../hocs/withLoadable.hoc';
import DuoAuthCallback from '../../DuoAuthCallback';
import { getCognitoLoginRoutes } from '@tekion/tap-cognito-lib/routes';
import withSuspense from '@tekion/tap-cognito-lib/hocs/withSuspense.hoc';
import BaseLogics from '@tekion/tap-cognito-lib/logics/base.logics';

const SignIn = withLoadable(React.lazy(() => import(/* webpackChunkName: "SignIn" */ '../../signIn')));
const StaticSignUp = withLoadable(React.lazy(() => import(/* webpackChunkName: "StaticSignUp" */ '../../SignUp')));
const Settings = withLayout(withLoadable(React.lazy(() => import(/* webpackChunkName: "Settings" */ '../../Settings'))));
// const Profile = withLayout(withLoadable(React.lazy(() => import(/* const "CognitoLogin" */ '../../Settings/Profile'))));
const TaskManagement = withLoadable(React.lazy(() => import(/* webpackChunkName: "TaskManagement" */ '../../Settings/TaskManagement')));
const Calendar = withLoadable(React.lazy(() => import(/* webpackChunkName: "Calendar" */ '../../Settings/Calendar')));
const SupportPortal = withLoadable(React.lazy(() => import(/* webpackChunkName: "SupportPortal" */ '../../Settings/SupportPortal')));
const NotificationSettings = withLoadable(React.lazy(() => import(/* webpackChunkName: "NotificationSettings" */ '../../Settings/NotificationSettings/NotificationSettings')));
const TapApplicationsContainer = withLayout(withLoadable(React.lazy(() => import(/* webpackChunkName: "TapApplicationsContainer" */ '../../TapApplictions/TapApplicationsContainer'))));
const TapApplications = withLayout(withLoadable(React.lazy(() => import(/* webpackChunkName: "TapApplications" */ '../../TapApplictions/TapApplications'))));
const Listing = withLayout(withLoadable(React.lazy(() => import(/* webpackChunkName: "Listing" */ '../../TapApplictions/Listing'))));
const CreateModule = withLayout(withLoadable(React.lazy(() => import(/* webpackChunkName: "CreateModule" */ '../../TapApplictions/CreateModule'))));
const ReleaseVersion = withLayout(withLoadable(React.lazy(() => import(/* webpackChunkName: "ReleaseVersion" */ '../../TapApplictions/ReleaseVersion'))));
const Home = withLayout(withLoadable(React.lazy(() => import(/* webpackChunkName: "Home" */ '../../Home/home'))));
const CardManagement = withLayout(withLoadable(React.lazy(() => import(/* webpackChunkName: "CardManagement" */ '../../TapApplictions/CardManagement/CardManagement'))));
const CreateForm = withLayout(withLoadable(React.lazy(() => import(/* webpackChunkName: "CreateForm" */ '../../TapApplictions/CardManagement/CreateForm'))));
const GroupManagement = withLayout(withLoadable(React.lazy(() => import(/* webpackChunkName: "GroupManagement" */ '../../TapApplictions/GroupManagement/GroupManagement'))));
const GroupCreateForm = withLayout(withLoadable(React.lazy(() => import(/* webpackChunkName: "GroupCreateForm" */ '../../TapApplictions/GroupManagement/GroupCreateForm'))));
const TapConfigurations = withLayout(withLoadable(React.lazy(() => import(/* webpackChunkName: "TapConfigurations" */ '../../TapApplictions/TapConfigurations'))));
const MobileConfigurations = withLayout(withLoadable(React.lazy(() => import(/* webpackChunkName: "MobileConfigurations" */ '../../TapApplictions/MobileConfigurations/index'))));
const MobileConfigurationsCreate = withLayout(withLoadable(React.lazy(() => import(/* webpackChunkName: "MobileConfigurationsCreate" */ '../../TapApplictions/MobileConfigurations/MobileConfigurationsCreate/index'))));
const TapConfigCreateForm = withLayout(withLoadable(React.lazy(() => import(/* webpackChunkName: "TapConfigCreateForm" */ '../../TapApplictions/TapConfigurations/TapConfigCreateForm'))));
const Desktop = withLayout(withLoadable(React.lazy(() => import(/* webpackChunkName: "Desktop" */ '../../Desktop'))));
const UserManagement = withLayout(withLoadable(React.lazy(() => import(/* webpackChunkName: "UserManagement" */ '../../UserManagement'))));
const UserImageMigration = withLayout(withLoadable(React.lazy(() => import(/* webpackChunkName: "UserImageMigration" */ '../../UserImageMigration/UserImageMigration.container'))));
const CalendarAvailability = withLoadable(React.lazy(() => import(/* webpackChunkName: "CalendarAvailability" */ '../../Settings/Availability')));
const FeatureTracker = withLoadable(React.lazy(() => import(/* webpackChunkName: "FeatureTracker" */ '../../Settings/FeatureTracker')));

const SQLifyRedirect = () => {
  const { memoryKey } = useParams();
  useEffect(() => {
    window.location.href = `/platform/sqlify${memoryKey ? `/${memoryKey}` : ''}`;
  }, [memoryKey]);

  return null;
};

const { COGNITO_LOGIN, APPLICATION_TYPE, KEEP_TAP_AND_COGNITO_BOTH } = process.env;
let defaultSettingsRedirect = '/app/settings/calendar';
if (COGNITO_LOGIN && APPLICATION_TYPE !== 'workspace') {
  defaultSettingsRedirect = '/app/settings/supportportal';
} else if (COGNITO_LOGIN) {
  defaultSettingsRedirect = '/app/settings/taskmanagement';
}

const RouteWithPermission = props => {
  const isInitialized = useMemo(() => {
    BaseLogics.init(props.history);
    return true;
  }, [props.history]);

  const renderRoutes = () => (
    <>
      <Switch>
        {getCognitoLoginRoutes()}
        <Route exact path="/app/duo/callback" component={DuoAuthCallback} />
        <Route exact path="/signup" component={StaticSignUp} />
        <Redirect exact from="/app/settings/profile" to="/app/settings/calendar" />
        <Redirect exact from="/app/settings" to={defaultSettingsRedirect} />
        <Route
          exact
          path="/app/user-management"
          component={() => <UserManagement apiBaseUrl={process.env.BASE_URL} />}
        />
        <Route
          exact
          path="/app/user-migration"
          component={() => <UserImageMigration apiBaseUrl={process.env.BASE_URL} />}
        />
        <Route
          path="/app/settings"
          component={() => (
            <Settings>
              <Route
                path="/app/settings/availability"
                exact
                component={() => <CalendarAvailability apiBaseUrl={process.env.BASE_URL} {...props} />}
              />
              <Route
                path="/app/settings/taskmanagement"
                exact
                component={() => (
                  <TaskManagement apiBaseUrl={process.env.BASE_URL} />
                )}
              />
              <Route
                path="/app/settings/calendar"
                exact
                component={() => <Calendar apiBaseUrl={process.env.BASE_URL} {...props} />}
              />
              <Route
                path="/app/settings/supportportal"
                exact
                component={() => <SupportPortal apiBaseUrl={process.env.BASE_URL} />}
              />
              <Route
                path="/app/settings/notification"
                exact
                component={() => <NotificationSettings apiBaseUrl={process.env.BASE_URL} />}
              />
              <Route
                path="/app/settings/feature-tracker"
                exact
                component={() => <FeatureTracker apiBaseUrl={process.env.BASE_URL_2} />}
              />
            </Settings>
          )}
        />
        <Route exact path="/app/callback" component={LoginCallback} />
        <Route
          exact
          path="/app/singlesignon"
          component={() => (
            <SingleSignon
              BASE_URL={process.env.BASE_URL}
              AUTHORIZATION_SERVER_ID={process.env.AUTHORIZATION_SERVER_ID}
              authenticateApiUrl="/tapapi/v2/usermanagement/authenticate-okta-user"
              clientId={process.env.OKTA_CLIENT_ID}
              redirectUri={`${window.location.origin}/app/singlesignon`}
              scope="openid profile email"
            />
          )}
        />
        <Route
          path="/app/tapapplications"
          component={() => (
            <TapApplications>
              <Switch>
                <Route
                  exact
                  path="/app/tapapplications"
                  component={() => <TapApplicationsContainer apiBaseUrl={process.env.BASE_URL} />}
                />
                <Route
                  exact
                  path="/app/tapapplications/releaseversion"
                  component={() => <ReleaseVersion apiBaseUrl={process.env.BASE_URL} />}
                />
                <Route
                  exact
                  path="/app/tapapplications/cards"
                  component={() => <CardManagement apiBaseUrl={process.env.BASE_URL} />}
                />
                <Route
                  exact
                  path="/app/tapapplications/cards/create"
                  component={() => <CreateForm apiBaseUrl={process.env.BASE_URL} />}
                />
                <Route
                  exact
                  path="/app/tapapplications/cards/:appKey/edit"
                  component={() => <CreateForm apiBaseUrl={process.env.BASE_URL} />}
                />
                <Route
                  exact
                  path="/app/tapapplications/groups"
                  component={() => <GroupManagement apiBaseUrl={process.env.BASE_URL} />}
                />
                <Route
                  exact
                  path="/app/tapapplications/groups/create"
                  component={() => <GroupCreateForm apiBaseUrl={process.env.BASE_URL} />}
                />
                <Route
                  exact
                  path="/app/tapapplications/groups/:appKey/edit"
                  component={() => <GroupCreateForm apiBaseUrl={process.env.BASE_URL} isEdit />}
                />
                <Route
                  exact
                  path="/app/tapapplications/tapconfig"
                  component={() => <TapConfigurations apiBaseUrl={process.env.BASE_URL} />}
                />
                <Route
                  exact
                  path="/app/tapapplications/tapconfig/create"
                  component={() => <TapConfigCreateForm apiBaseUrl={process.env.BASE_URL} isCreate />}
                />
                <Route
                  exact
                  path="/app/tapapplications/tapconfig/:module/edit"
                  component={() => <TapConfigCreateForm apiBaseUrl={process.env.BASE_URL} isEdit />}
                />
                <Route
                  exact
                  path="/app/tapapplications/mobileconfigurations"
                  component={() => <MobileConfigurations apiBaseUrl={process.env.BASE_URL} />}
                />
                <Route
                  exact
                  path="/app/tapapplications/mobileconfigurations/create"
                  component={() => <MobileConfigurationsCreate apiBaseUrl={process.env.BASE_URL} />}
                />
                <Route
                  exact
                  path="/app/tapapplications/mobileconfigurations/edit/:platform"
                  component={() => <MobileConfigurationsCreate apiBaseUrl={process.env.BASE_URL} isEdit />}
                />
                <Route
                  exact
                  path="/app/tapapplications/:feature"
                  component={() => <Listing apiBaseUrl={process.env.BASE_URL} />}
                />
                <Route
                  exact
                  path="/app/tapapplications/:feature/create"
                  component={() => <CreateModule apiBaseUrl={process.env.BASE_URL} />}
                />
                <Route
                  exact
                  path="/app/tapapplications/:feature/:module/edit"
                  component={() => <CreateModule apiBaseUrl={process.env.BASE_URL} />}
                />
              </Switch>
            </TapApplications>
          )}
        />
        <Route
          exact
          path="/app/home"
          component={() => <Home />}
        />
        <Route
          exact
          path="/app/setup"
          component={() => (
            <PostAuthentication
              BASE_URL={process.env.BASE_URL}
              authenticateApiUrl="/tapapi/v2/usermanagement/authenticate-okta-user"
            />
          )}
        />
        <Route
          exact
          path="/app/sqlify/:memoryKey?"
          component={() => <SQLifyRedirect />}
        />
        <Route
          exact
          path="/app/desktop/:app"
          component={routerProps => (
            <Desktop {...routerProps} />
          )}
        />
        <Redirect exact from="/" to="/login" />
        <Route
          path=""
          component={routerProps => <div style={{ padding: '4rem' }}> <ErrorPage {...routerProps} notFound /></div>}
        />
      </Switch>
    </>
  );

  if (!isInitialized) {
    return null;
  }

  if (COGNITO_LOGIN === 'true' && KEEP_TAP_AND_COGNITO_BOTH !== 'true') {
    return renderRoutes();
  }

  return (
    <Security
      oktaAuth={TokenManager.getAuthClient()}
      restoreOriginalUri={() => { }}
    >
      {renderRoutes()}
    </Security>
  );
};

export default withSuspense(RouteWithPermission);
